import { Input } from "@src/lib/ui/Input";
import { MediaFragment } from "@src/fragments.generated";
import { useDebouncedInputValue } from "@src/lib/hooks/useDebouncedInputValue";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import gql from "graphql-tag";
import {
  useAddMediaResourcesTrainingResourcesQuery,
  AddMediaResources_TrainingResourceFragment,
  AddMediaResources_LibraryItemFragment,
} from "./Resources.generated";
import Text from "@src/lib/ui/Text";
import { getLinkType, LinkType } from "@src/lib/utils/files";
import { Skeleton } from "@src/lib/ui/Skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@src/lib/ui/Select";
import { ResourceThumbnail } from "@src/features/contentLibrary/ui/ResourceThumbnail";
import TrainingResourcePDFRichTextExport from "@src/features/libraryItem/resource/pdf/TrainingResourcePDFRichTextExport";
import { extractBodyContent } from "@src/features/libraryItem/resource/build/TrainingResourceRichTextEditor";
import {
  TrainingResourceVersionType,
  LibraryItemType,
  LibrarySearchMode,
} from "@src/types.generated";

type FileType = "All" | "PDFs" | "Images" | "Videos" | "Opus Docs";

const FILE_TYPES: FileType[] = ["All", "Opus Docs", "PDFs", "Images", "Videos"];

type Sort = "Last Edited" | "Name" | "Type";

const SORT_TO_INPUT: Record<Sort, { column: string; descending: boolean }> = {
  "Last Edited": { column: "lastEdited", descending: true },
  Name: { column: "name", descending: false },
  Type: { column: "type", descending: false },
};

type Props = {
  onResourceClick: (
    trainingResource: AddMediaResources_TrainingResourceFragment,
  ) => void;
  filterResources?: LinkType[];
  disableFileTypeSelect?: boolean;
};

const LINK_TYPE_FILE_TYPE_MAP: Record<LinkType, FileType | null> = {
  [LinkType.PDF]: "PDFs",
  [LinkType.IMAGE]: "Images",
  [LinkType.VIDEO]: "Videos",
  [LinkType.HOSTED_VIDEO]: "Videos",
  [LinkType.MUX_VIDEO]: "Videos",
  [LinkType.GIF]: "Images",
  [LinkType.AUDIO]: "All",
  [LinkType.LINK]: "All",
  [LinkType.VCARD]: "All",
  [LinkType.EMOJI]: null,
  [LinkType.WEBSITE]: "All",
};

function getFileType(tr: AddMediaResources_TrainingResourceFragment): FileType {
  if (
    tr.publishedVersion?.type === TrainingResourceVersionType.RichTextDocument
  ) {
    return "Opus Docs";
  }
  const mediaUrl = tr.publishedVersion?.media?.mediaUrls?.en;
  if (!mediaUrl) {
    return "All";
  }

  const linkType = getLinkType(mediaUrl);
  if (linkType === LinkType.PDF) {
    return "PDFs";
  }
  if ([LinkType.GIF, LinkType.IMAGE].includes(linkType)) {
    return "Images";
  }
  if (
    [LinkType.VIDEO, LinkType.HOSTED_VIDEO, LinkType.MUX_VIDEO].includes(
      linkType,
    )
  ) {
    return "Videos";
  }

  return "All";
}

const Loading: FC = () => <Skeleton className="h-[128px] w-[107px] p-2" />;
const PAGE_SIZE = 40;

const Resources: FC<Props> = (props) => {
  const availableFileTypes = useMemo<FileType[]>(() => {
    if (!props.filterResources || props.filterResources.length === 0) {
      return FILE_TYPES;
    }
    const allowed = new Set(
      props.filterResources
        .map((linkType) => LINK_TYPE_FILE_TYPE_MAP[linkType])
        .filter((mapped): mapped is FileType => mapped !== null),
    );
    return FILE_TYPES.filter((candidate) => allowed.has(candidate));
  }, [props.filterResources]);
  const [fileType, setFileType] = useState<FileType>(() => {
    const preselected = props.filterResources?.length
      ? LINK_TYPE_FILE_TYPE_MAP[props.filterResources[0]]
      : "All";
    return preselected && availableFileTypes.includes(preselected)
      ? preselected
      : availableFileTypes[0] || "All";
  });

  useEffect(() => {
    if (!availableFileTypes.includes(fileType)) {
      setFileType(availableFileTypes[0] || "All");
    }
  }, [availableFileTypes, fileType]);

  const [sort, setSort] = useState<Sort>("Last Edited");
  const {
    value: search,
    setValue: setSearchValue,
    debouncedValue: debouncedSearch,
  } = useDebouncedInputValue({ initialValue: "", delay: 300 });
  const isSearching = !!debouncedSearch;
  const sortInput = isSearching ? undefined : SORT_TO_INPUT[sort];
  const [numVisibleItems, setNumVisibleItems] = useState(PAGE_SIZE);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { data, loading } = useAddMediaResourcesTrainingResourcesQuery({
    variables: {
      input: {
        filters: {
          itemType: { value: LibraryItemType.TrainingResource },
        },
        ...(debouncedSearch ? { search: { value: debouncedSearch } } : {}),
        ...(sortInput ? { sort: sortInput } : {}),
        searchMode: LibrarySearchMode.NameDescriptionAndContent,
      },
    },
  });
  const libraryItems: AddMediaResources_LibraryItemFragment[] = useMemo(
    () => data?.AdminLibrary?.objects || [],
    [data?.AdminLibrary?.objects],
  );

  const totalCount = useMemo(
    () => data?.AdminLibrary?.totalCount || 0,
    [data?.AdminLibrary?.totalCount],
  );

  const filteredLibraryItems: AddMediaResources_LibraryItemFragment[] = useMemo(
    () =>
      libraryItems.filter((r) => {
        const mediaUrl =
          r.trainingResource?.publishedVersion?.media?.mediaUrls?.en;
        if (!r.trainingResource) {
          return false;
        }
        const currentFileType = getFileType(r.trainingResource);
        let filter = true;
        if (props.filterResources && mediaUrl) {
          filter =
            filter && props.filterResources?.includes(getLinkType(mediaUrl));
        }
        if (fileType !== "All") {
          if (fileType === "PDFs") {
            filter = filter && currentFileType === "PDFs";
          }
          if (fileType === "Images") {
            filter = filter && currentFileType === "Images";
          }
          if (fileType === "Videos") {
            filter = filter && currentFileType === "Videos";
          }
          if (fileType === "Opus Docs") {
            filter = filter && currentFileType === "Opus Docs";
          }
        }
        return filter;
      }),
    [props.filterResources, libraryItems, fileType],
  );
  const visibleLibraryItems = useMemo(() => {
    return filteredLibraryItems.slice(0, numVisibleItems);
  }, [filteredLibraryItems, numVisibleItems]);
  const loadMore = useCallback(() => {
    if (visibleLibraryItems.length >= totalCount) {
      return;
    }
    setNumVisibleItems(numVisibleItems + PAGE_SIZE);
  }, [visibleLibraryItems.length, totalCount, numVisibleItems]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (
        container.scrollTop + container.clientHeight >=
          container.scrollHeight - 200 && // 200px threshold
        !loading
      ) {
        loadMore();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [loadMore, loading]);
  return (
    <>
      <Input
        placeholder="Search resources..."
        className="mb-2 w-full"
        value={search}
        onChange={(e) => setSearchValue(e.target.value)}
      />
      <div className="mb-2 flex gap-1">
        <Select
          value={fileType}
          disabled={props.disableFileTypeSelect}
          onValueChange={(v) => {
            const next = v as FileType;
            if (availableFileTypes.includes(next)) {
              setFileType(next);
            }
          }}
        >
          <SelectTrigger className="mr-1">
            <>
              <span className="text-muted-foreground">File type:</span>
              <span className="mx-1">{fileType}</span>
            </>
          </SelectTrigger>
          <SelectContent className="z-[1000]">
            {availableFileTypes.map((availableFileType) => (
              <SelectItem key={availableFileType} value={availableFileType}>
                {availableFileType}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!isSearching && (
          <Select
            defaultValue="Last Edited"
            value={sort}
            onValueChange={(v) => setSort(v as Sort)}
          >
            <SelectTrigger>
              <>
                <span className="text-muted-foreground">Sort by:</span>
                <span className="mx-1">{sort}</span>
              </>
            </SelectTrigger>
            <SelectContent className="z-[1000]">
              <SelectItem value={"Last Edited"}>Last Edited</SelectItem>
              <SelectItem value={"Name"}>Name</SelectItem>
              <SelectItem value={"Type"}>Type</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      <div
        ref={scrollContainerRef}
        className="flex max-h-full w-full flex-wrap justify-start gap-2 overflow-y-auto"
      >
        {loading && libraryItems.length === 0 && (
          <>
            {Array.from({ length: 11 }).map((_, i) => (
              <Loading key={`add-media-loading-${i}`} />
            ))}
          </>
        )}
        {!loading &&
          visibleLibraryItems.length > 0 &&
          visibleLibraryItems.map((item) => {
            if (!item.trainingResource?.publishedVersion) {
              return null;
            }
            return (
              <Resource
                key={`dialog-file-list-${item.trainingResource.publishedVersion.id}`}
                onClick={() =>
                  item.trainingResource &&
                  props.onResourceClick(item.trainingResource)
                }
                version={item.trainingResource.publishedVersion}
                name={item.name.en}
              />
            );
          })}
        {!loading && visibleLibraryItems.length === 0 && (
          <Text variant="compact-description" className="text-muted-foreground">
            No resources found
          </Text>
        )}
      </div>
    </>
  );
};
const MediaResource: FC<{
  media: MediaFragment;
  name: string;
  onClick: () => void;
}> = (props) => {
  return (
    <div
      className="flex h-[128px] w-[107px] cursor-pointer flex-col rounded-md bg-muted p-2"
      onClick={props.onClick}
    >
      <div className="max-h-[80px] overflow-hidden">
        <ResourceThumbnail media={props.media} />
      </div>
      <Text
        variant="fine-print-description"
        className="mt-auto truncate"
        title={props.name || ""}
      >
        {props.name || ""}
      </Text>
    </div>
  );
};
const Resource: FC<{
  version: NonNullable<
    AddMediaResources_TrainingResourceFragment["publishedVersion"]
  >;
  name: string;
  onClick: () => void;
}> = (props) => {
  if (
    props.version.type === TrainingResourceVersionType.Media &&
    props.version.media
  ) {
    return (
      <MediaResource
        media={props.version.media}
        name={props.name}
        onClick={props.onClick}
      />
    );
  }
  if (
    props.version.type === TrainingResourceVersionType.RichTextDocument &&
    props.version.richTextDocument
  ) {
    return (
      <div
        className="flex h-[128px] w-[107px] cursor-pointer flex-col rounded-md bg-muted p-2"
        onClick={props.onClick}
      >
        <div className="max-h-[80px] w-[120px] min-w-[120px] overflow-hidden">
          <div className="origin-top-left scale-[0.1]">
            <TrainingResourcePDFRichTextExport
              title={props.name || ""}
              description={""}
              html={extractBodyContent(
                props.version.richTextDocument.content?.en || "",
              )}
            />
          </div>
        </div>
        <Text
          variant="fine-print-description"
          className="mt-auto truncate"
          title={props.name || ""}
        >
          {props.name || ""}
        </Text>
      </div>
    );
  }
  return null;
};

export default Resources;

gql`
  fragment AddMediaResources_TrainingResource on TrainingResource {
    id
    publishedVersion {
      id
      type
      media {
        ...Media
      }
      richTextDocument {
        ...RichTextDocument
      }
    }
    libraryItem {
      id
      type
      name {
        en
      }
      coverImage {
        ...CoverImage
      }
      ...TrainingResourceAvatar_LibraryItem
    }
  }
  fragment AddMediaResources_LibraryItem on LibraryItem {
    id
    createdAt
    name {
      ...TranslationSet
    }
    description {
      ...TranslationSet
    }
    trainingResource {
      ...AddMediaResources_TrainingResource
    }
  }
  query AddMediaResourcesTrainingResources(
    $input: AdminLibraryInput!
    $pagination: PaginationInput
  ) {
    AdminLibrary(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...AddMediaResources_LibraryItem
      }
    }
  }
`;
